import { FC } from 'react';
import Modal from '@/components/modals/Modal/Modal.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import useAdminOrgContext from '@/api/admin/useAdminOrgContext.ts';

type Props = {
  orgId: string;
  orgName: string;
  isOpen: boolean;
  onClose: () => void;
};

type SectorData = {
  industry?: string;
  market_dynamics?: string;
  regulatory_environment?: string;
  customer_type?: string;
  competitive_landscape?: string;
};

type MetaData = {
  company_size?: string;
  team_structure?: string;
  stage?: string;
  geography?: string;
  revenue_range?: string;
  operating_model?: string;
};

type OperatingConstraints = {
  kpis?: string[];
  other?: string;
};


function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="font-dm font-bold text-[11px] uppercase tracking-widest text-[#2b3440]/50">{label}</span>
      <p className="font-dm text-sm text-[#2b3440] leading-relaxed">{value}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="font-dm font-bold text-[11px] uppercase tracking-widest text-[#2b3440]/50">{title}</span>
      <div className="flex flex-col gap-3 pl-3 border-l-2 border-[rgba(204,204,204,0.6)]">{children}</div>
    </div>
  );
}

function Placeholder({ text }: { text: string }) {
  return <p className="font-dm text-sm text-[#2b3440]/35 italic">{text}</p>;
}

const OrgDetailsModal: FC<Props> = ({ orgId, orgName, isOpen, onClose }) => {
  const { data: ctx, isLoading } = useAdminOrgContext(isOpen ? orgId : null);

  const sector = ctx?.sector as SectorData | null | undefined;
  const metadata = ctx?.metadata as MetaData | null | undefined;
  const constraints = ctx?.operating_constraints as OperatingConstraints | null | undefined;
  const kpis = constraints?.kpis ?? [];

  return (
    <Modal.Wrapper isOpen={isOpen} onClose={onClose} showCloseButton closeable wrapperClassName="max-w-lg w-full">
      <Modal.Title>
        <div className="flex flex-col gap-0.5">
          <h2 className="font-newsreader text-[22px] font-extrabold leading-snug text-[rgba(0,0,0,0.87)]">
            {orgName}
          </h2>
          <p className="font-dm text-sm text-[rgba(0,0,0,0.5)]">Organizational Operating Profile</p>
        </div>
      </Modal.Title>

      {isLoading ? (
        <LoaderWrapper isLoading className="py-10" />
      ) : !ctx ? (
        <p className="font-dm text-sm text-[#2b3440]/50 py-6 text-center">No organizational profile recorded yet.</p>
      ) : (
        <div className="flex flex-col gap-5 pb-2">

          {/* 1. Strategic Orientation */}
          <Section title="Strategic Orientation">
            {ctx.strategic_orientation
              ? <Field label="Orientation" value={ctx.strategic_orientation} />
              : <Placeholder text="No strategic orientation recorded." />}
          </Section>

          {/* 2. External Operating Environment */}
          <Section title="External Operating Environment">
            {sector ? (
              <>
                <Field label="Economic Conditions" value={sector.industry} />
                <Field label="Market Velocity" value={sector.market_dynamics} />
                <Field label="Demand Environment" value={sector.customer_type} />
                <Field label="External Forces Creating Uncertainty" value={sector.competitive_landscape} />
              </>
            ) : (
              <Placeholder text="No external operating environment recorded." />
            )}
          </Section>

          {/* 3. Governance Constraints */}
          <Section title="Governance Constraints">
            {metadata ? (
              <>
                <Field label="Organizational Lifecycle" value={metadata.stage} />
                <Field label="Company Size" value={metadata.company_size} />
                <Field label="Team Structure" value={metadata.team_structure} />
                <Field label="Economic Scale" value={metadata.revenue_range} />
                <Field label="Value Creation System" value={metadata.operating_model} />
                <Field label="Operational Reach" value={metadata.geography} />
              </>
            ) : (
              <Placeholder text="No governance constraints recorded." />
            )}
          </Section>

          {/* 4. Operational Signals */}
          <Section title="Operational Signals">
            {kpis.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {kpis.map((kpi) => (
                  <span
                    key={kpi}
                    className="inline-block px-3 py-1 rounded-full bg-[#f4f9ff] font-dm text-xs text-[#2b3440] border border-[rgba(204,204,204,0.5)]"
                  >
                    {kpi}
                  </span>
                ))}
              </div>
            ) : (
              <Placeholder text="No operational signals recorded." />
            )}
          </Section>

          {/* 5. Regulatory Exposure */}
          <Section title="Regulatory Exposure">
            {sector?.regulatory_environment ? (
              <Field label="Regulatory Context" value={sector.regulatory_environment} />
            ) : (
              <Placeholder text="No regulatory exposure recorded." />
            )}
          </Section>

          {/* 6. Decision Authorities */}
          <Section title="Decision Authorities">
            <Placeholder text="Decision authority configuration pending institutional setup." />
          </Section>

          {/* 7. Strategic Priorities */}
          <Section title="Strategic Priorities">
            {ctx.strategic_orientation ? (
              <Field label="Active Priority" value={ctx.strategic_orientation} />
            ) : (
              <Placeholder text="Strategic priorities derive from orientation and operational signals." />
            )}
          </Section>

        </div>
      )}
    </Modal.Wrapper>
  );
};

export default OrgDetailsModal;
